import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, take, tap } from 'rxjs/operators';
import { ProgresoEndPoint } from '../models/ProgresoEndPoint';

@Injectable({
  providedIn: 'root',
})
export class EndPointService implements OnDestroy {
  protected _endPoints: Array<ProgresoEndPoint<any>> = new Array<ProgresoEndPoint<any>>();
  public endpointSubject = new Subject();

  private subscriptions: Subscription[] = [];

  constructor(protected _http: HttpClient) { }

  /**
   * @param name The name of the endPoint
   * @param path The path to the backend endPoint
   * @param enableCache Will enableCaching for this endPoint (Do not use caching for update or create endPoints!)
   * @param relations Wich endPoints need to update after this endPoint is called. Only updates endPoints with cache enabled.
   */
  public registerEndPoint<T>(
    name: string,
    path: string,
    enableCache: boolean = true,
    relations: string[] = []
  ): void {
    if (this._endPoints[name]) {
      console.warn(
        `[EndPointsService]: An endpoint is already defined under this name (${name})`
      );
    }
    const endPoint: ProgresoEndPoint<T> = new ProgresoEndPoint<T>(
      name,
      path,
      enableCache,
      relations
    );
    this._endPoints[name] = endPoint;
  }

  /**
   * @param name The name of the registered endPoint
   * @param data The data that is added to the body of the call.
   * @param fetch Overwrite the data in the cache.
   */
  public call<T>(
    name: string,
    data: any = null,
    fetch: boolean = false
  ): Observable<T> {
    if (this._endPoints[name]) {
      const endPoint: ProgresoEndPoint<T> = this._endPoints[name];
      endPoint.timesCalled++;
      if (this.canCall<T>(endPoint, data, fetch)) {
        endPoint.timesRefreshed++;
        endPoint.lastData = data;
        this.cacheCall<T>(endPoint, data);
        this.makeCall<T>(endPoint, data);
      } else {
        const cachedCall: BehaviorSubject<T> = this.checkCash<T>(
          endPoint,
          data
        );
        if (cachedCall) {
          endPoint.lastResult$ = cachedCall;
        }
      }
      return endPoint.lastResult$
        .asObservable()
        .pipe(filter((response: T) => response !== undefined));
    }
  }

  public callBlob<T>(
    name: string,
    data: any = null,
    fetch: boolean = false
  ): Observable<T> {
    if (this._endPoints[name]) {
      const endPoint: ProgresoEndPoint<T> = this._endPoints[name];

      this.subscriptions.push(
        this._http
          .post<T>(endPoint.path, data, { responseType: 'blob' as 'json' })
          .pipe(
            take(1),
            tap((responseData: T) => {
              let resultSubject$ = endPoint.lastResult$;
              if (endPoint.cacheable && this.isCachableData(data)) {
                resultSubject$ = endPoint.subjects.get(this.dataToString(data));
              }
              resultSubject$.next(responseData);
              this.updateRelations(endPoint.relations);
              this.endpointSubject.next(void 0);
            }),
            catchError(this.errorHandler)
          )
          .subscribe()
      );
      return endPoint.lastResult$
        .asObservable()
        .pipe(filter((response: T) => response !== undefined));
    }
  }

  public clearCache() {
    for (let point of this._endPoints) {
      point.subjects.forEach((e) => e.unsubscribe());
    }
    this._endPoints.length = 0;
  }

  protected makeCall<T>(endPoint: ProgresoEndPoint<T>, data: any): void {
    this.subscriptions.push(
      this._http
        .post<T>(endPoint.path, data)
        .pipe(
          take(1),
          tap((responseData: T) => {
            let resultSubject$ = endPoint.lastResult$;
            if (endPoint.cacheable && this.isCachableData(data)) {
              resultSubject$ = endPoint.subjects.get(this.dataToString(data));
            }
            resultSubject$.next(responseData);
            this.updateRelations(endPoint.relations);
            this.endpointSubject.next(void 0);
          }),
          catchError(this.errorHandler)
        )
        .subscribe()
    );
  }

  protected updateRelations(relations: string[]): void {
    relations.forEach((relation: string) => {
      const endPoint: ProgresoEndPoint<any> = this._endPoints[relation];
      if (endPoint && endPoint.cacheable) {
        for (const dataString of endPoint.subjects.keys()) {
          const data: any = this.StringToData(dataString);
          this.call(endPoint.name, data, true);
        }
      }
    });
  }

  protected cacheCall<T>(endPoint: ProgresoEndPoint<T>, data: any): void {
    let subject$: BehaviorSubject<T>;
    if (this.isCachableData(data) && endPoint.cacheable) {
      if (endPoint.subjects) {
        subject$ = endPoint.subjects.get(this.dataToString(data));
      }
      if (!subject$) {
        endPoint.subjects.set(
          this.dataToString(data),
          new BehaviorSubject<T>(undefined)
        );
        subject$ = endPoint.subjects.get(this.dataToString(data));
      }
    } else {
      if (!endPoint.cacheable) {
        endPoint.lastResult$ = new BehaviorSubject<T>(undefined);
      }
      subject$ = endPoint.lastResult$;
    }
    endPoint.lastResult$ = subject$;
  }

  protected canCall<T>(
    endPoint: ProgresoEndPoint<T>,
    data: any,
    fetch: boolean
  ): boolean {
    if (fetch) {
      return true;
    }
    if (this.checkCash(endPoint, data) === null) {
      if (this.isCachableData(data)) {
        return true;
      }
      return endPoint.timesRefreshed === 0;
    }
  }

  protected checkCash<T>(
    endPoint: ProgresoEndPoint<T>,
    data: any
  ): BehaviorSubject<T> {
    if (this.isCachableData(data)) {
      const result: BehaviorSubject<T> = endPoint.subjects.get(
        this.dataToString(data)
      );
      return result ? result : null;
    }
    return null;
  }

  protected isCachableData(data: any) {
    return data !== null;
  }

  protected errorHandler(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error || 'Server Error');
  }

  getCircularReplacer = () => {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return;
        }
        seen.add(value);
      }
      return value;
    };
  };

  protected dataToString(data: any): string {
    if (typeof data === 'number' || typeof data === 'string') {
      return data.toString();
    }
    return JSON.stringify(data, this.getCircularReplacer());
  }

  protected StringToData(dataString: string): any {
    let data: any;
    try {
      data = JSON.parse(dataString);
    } catch (error) {
      return dataString;
    }
    return data;
  }


  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    }

  }
}
